import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
  Drawer,
  Dialog,
  Button,
  CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getApplicantReport, getApplicantPersonality, getApplicantTopicScore, getApplicantScoreBand } from '../../redux/actions/b2bActions';

interface ReportViewProps {
  open: boolean;
  onClose: () => void;
  candidateName?: string;
  applicantId?: number;
  reportData?: any;
  personalityData?: any;
  topicScoreData?: any;
  scoreBandData?: any;
}

interface SkillTrait {
  name: string;
  description: string;
  score: number;
  grade: 'HIGH' | 'MOD' | 'LOW';
}

interface PersonalityType {
  title: string;
  description: string;
  careerAvenues: string[];
  icon: string;
}

// Report Content Component
const ReportContent: React.FC<{ 
  candidateName: string; 
  isMobile: boolean; 
  onClose: () => void; 
  applicantId?: number;
  reportData?: any;
  personalityData?: any;
  topicScoreData?: any;
  scoreBandData?: any;
}> = ({ 
  candidateName, 
  isMobile,
  onClose,
  applicantId,
  reportData,
  personalityData,
  topicScoreData,
  scoreBandData
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [localReportData, setLocalReportData] = useState(reportData);
  const [localPersonalityData, setLocalPersonalityData] = useState(personalityData);
  const [localTopicScoreData, setLocalTopicScoreData] = useState(topicScoreData);
  const [localScoreBandData, setLocalScoreBandData] = useState(scoreBandData);

  // Fetch all report data when component mounts or applicantId changes
  useEffect(() => {
    console.log('useEffect triggered:', { applicantId, localReportData, localPersonalityData, localTopicScoreData, localScoreBandData });
    if (applicantId) {
      setLoading(true);
      
      let completedCalls = 0;
      const totalCalls = 4;
      
      const checkAllCallsComplete = () => {
        completedCalls++;
        if (completedCalls === totalCalls) {
          setLoading(false);
        }
      };
      
      // Call all APIs in parallel
      dispatch(getApplicantReport(
        applicantId,
        (response: any) => {
          console.log('Applicant detail data loaded:', response);
          setLocalReportData(response);
          checkAllCallsComplete();
        },
        () => {
          console.error('Failed to load applicant detail data');
          checkAllCallsComplete();
        }
      ));
      
      dispatch(getApplicantPersonality(
        applicantId,
        (response: any) => {
          console.log('Personality data loaded:', response);
          setLocalPersonalityData(response);
          checkAllCallsComplete();
        },
        () => {
          console.error('Failed to load personality data');
          checkAllCallsComplete();
        }
      ));
      
      dispatch(getApplicantTopicScore(
        applicantId,
        (response: any) => {
          console.log('Topic score data loaded:', response);
          setLocalTopicScoreData(response);
          checkAllCallsComplete();
        },
        () => {
          console.error('Failed to load topic score data');
          checkAllCallsComplete();
        }
      ));
      
      dispatch(getApplicantScoreBand(
        applicantId,
        (response: any) => {
          console.log('Score band data loaded:', response);
          setLocalScoreBandData(response);
          checkAllCallsComplete();
        },
        () => {
          console.error('Failed to load score band data');
          checkAllCallsComplete();
        }
      ));
    }
  }, [applicantId, dispatch]);

  // Handle undefined candidateName
  const displayName = candidateName || "Candidate";

  // Use real API data instead of sample data
  // Handle different data structures: array directly, or object with result/personalityTypes property
  // IMPORTANT: All hooks must be called before any early returns
  const personalityTypes = useMemo(() => {
    if (!localPersonalityData) return [];
    
    // If it's already an array, use it directly
    if (Array.isArray(localPersonalityData)) {
      return localPersonalityData;
    }
    
    // Check for nested result array
    if (Array.isArray(localPersonalityData.result)) {
      return localPersonalityData.result;
    }
    
    // Check for personalityTypes property
    if (Array.isArray(localPersonalityData.personalityTypes)) {
      return localPersonalityData.personalityTypes;
    }
    
    // Check if result is an object with an array inside
    if (localPersonalityData.result && Array.isArray(localPersonalityData.result.personalityTypes)) {
      return localPersonalityData.result.personalityTypes;
    }
    
    // Check if result is an object with a data property containing array
    if (localPersonalityData.result && Array.isArray(localPersonalityData.result.data)) {
      return localPersonalityData.result.data;
    }
    
    // Check if result is an object with a data property containing array
    if (localPersonalityData.data && Array.isArray(localPersonalityData.data)) {
      return localPersonalityData.data;
    }
    
    // Try to find any array property
    for (const key in localPersonalityData) {
      if (Array.isArray(localPersonalityData[key]) && localPersonalityData[key].length > 0) {
        // Check if it looks like personality data (has displayPersonalityType or similar)
        const firstItem = localPersonalityData[key][0];
        if (firstItem && (firstItem.displayPersonalityType || firstItem.personalityType || firstItem.type)) {
          return localPersonalityData[key];
        }
      }
    }
    
    return [];
  }, [localPersonalityData]);
  
  const topicScores = localTopicScoreData?.result || localTopicScoreData?.topicScores || [];
  const scoreBands = localScoreBandData?.result || localScoreBandData?.scoreBands || [];
  
  // Debug logging
  console.log('Data extraction debug:', {
    localPersonalityData,
    personalityTypes,
    personalityTypesLength: personalityTypes?.length,
    localTopicScoreData,
    topicScores,
    localScoreBandData,
    scoreBands
  });

  // Show loading state - MUST be after all hooks
  if (loading) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '16px' : '24px',
        }}
      >
        <CircularProgress size={40} sx={{ color: '#756DE9', marginBottom: '16px' }} />
        <Typography
          variant="h6"
          sx={{
            color: '#374151',
            fontSize: isMobile ? '16px' : '18px',
            textAlign: 'center',
          }}
        >
          Loading Report Data...
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#6B7280',
            fontSize: isMobile ? '12px' : '14px',
            textAlign: 'center',
            marginTop: '8px',
          }}
        >
          Please wait while we fetch the personality assessment results
        </Typography>
      </Box>
    );
  }
  
  console.log('ReportContent render:', { 
    candidateName, 
    isMobile, 
    applicantId,
    localReportData, 
    localPersonalityData, 
    localTopicScoreData, 
    localScoreBandData,
    loading
  });

  // Convert topic scores to skills traits format
  const skillsTraits: SkillTrait[] = topicScores.map((score: any) => ({
    name: score.topic || "Unknown Topic",
    description: score.descriptors || "No description available",
    score: score.percentile || 0,
    grade: score.scoringBand || "UNKNOWN"
  }));

  // Use real score band data for verification scores
  const verificationScores = [
    { label: "Low social desirability", status: "valid", icon: "✓" },
    { label: "Moderate social desirability", status: "invalid", icon: "✗" },
    { label: "High social desirability", status: "invalid", icon: "✗" },
    { label: "Invalid profile", status: "invalid", icon: "✗" }
  ];

  const getGradeColor = (grade: string) => {
    switch (grade?.toUpperCase()) {
      case 'HIGH':
        return '#00C853'; // Green
      case 'MEDIUM':
      case 'MOD':
      case 'MODERATE':
        return '#FF9800'; // Amber/Orange
      case 'LOW':
        return '#D50000'; // Red
      default:
        return '#9E9E9E'; // Gray
    }
  };

  const getGradeTextColor = (grade: string) => {
    return '#FFFFFF'; // White text for all grades
  };

  try {
    return (
      <Box
        sx={{
          height: '100%',
          overflow: 'auto',
          padding: isMobile ? '16px' : '24px',
        }}
      >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid #E0E0E0',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: '600',
            color: '#374151',
            fontSize: isMobile ? '18px' : '20px',
          }}
        >
          {displayName} WorkWise Personality Profiler
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            padding: '4px',
            '&:hover': {
              backgroundColor: '#F3F4F6',
            },
          }}
        >
          <Close sx={{ fontSize: '20px', color: '#6B7280' }} />
        </IconButton>
      </Box>

      {/* Personality Type Section */}
      {personalityTypes.length > 0 ? (
        <Box sx={{ marginBottom: '24px' }}>
          {/* Personality Type Header */}
          <Box
            sx={{
              backgroundColor: '#E6E4FE',
              padding: isMobile ? '16px' : '20px',
              textAlign: 'center',
              borderRadius: '10px',
              marginBottom: '20px',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: '600',
                fontSize: isMobile ? '18px' : '22px',
                color: '#1F2235',
                textAlign: 'center',
                marginBottom: '10px',
              }}
            >
              {personalityTypes?.length === 1 && personalityTypes[0]?.displayPersonalityType === "No Personality Type"
                ? "Well Balanced Personality"
                : personalityTypes
                    ?.map((el: any) => el?.displayPersonalityType)
                    .reduce((acc: string[], curr: string, index: number, arr: string[]) => {
                      if (arr.length === 2 && index === 1) {
                        return [...acc, "and " + curr];
                      }
                      if (index === arr.length - 1 && arr.length > 1) {
                        return [...acc, "and " + curr];
                      }
                      return [...acc, curr];
                    }, [])
                    .join(personalityTypes.length === 2 ? " " : ", ")
              }
            </Typography>
            <img src="/images/profiler-image.png" alt="" width={isMobile ? "150px" : "200px"} />
          </Box>

          {/* Personality Type Details */}
          {personalityTypes.map((personality: any, idx: number) => (
            <Box key={idx} sx={{ marginBottom: '20px' }}>
              {/* Personality Image */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '10px',
                  backgroundImage: 'url("/images/seevi-logo-new.png")',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  padding: '20px',
                  borderRadius: '10px',
                }}
              >
                {personality?.imagePath ? (
                  <Box 
                    component="img" 
                    sx={{ height: isMobile ? 150 : 200 }} 
                    src={personality?.imagePath} 
                    alt={personality?.displayPersonalityType}
                    onError={(e: any) => {
                      e.target.src = "/images/connector-image.png";
                    }}
                  />
                ) : (
                  <Box 
                    component="img" 
                    sx={{ height: isMobile ? 150 : 200 }} 
                    src="/images/connector-image.png" 
                    alt="Personality"
                  />
                )}
              </Box>

              {/* Personality Description */}
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: '#F5F6FA',
                  padding: isMobile ? '16px' : '20px',
                  borderRadius: '10px',
                  backgroundImage: 'url("/images/seevi-logo-new.png")',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  marginBottom: '10px',
                }}
              >
                <img src="/images/File-vector.png" alt="" width="24px" />
                <Typography
                  sx={{
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '500',
                    color: '#000',
                    marginTop: '8px',
                    marginBottom: '8px',
                  }}
                >
                  {personality?.displayPersonalityType == "No Personality Type" ? "Well Balanced Personality" : personality?.displayPersonalityType}
                </Typography>
                {personality?.typeDescriptors?.split('\n').map((detail: string, index: number) => (
                  <Typography
                    key={index}
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: '400',
                      color: '#000000',
                      marginBottom: '4px',
                    }}
                  >
                    {detail}
                  </Typography>
                ))}
              </Paper>

              {/* Career Avenues */}
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: '#F5F6FA',
                  padding: isMobile ? '16px' : '20px',
                  borderRadius: '10px',
                  backgroundImage: 'url("/images/seevi-logo-new.png")',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <img src="/images/Briefcase-Vector.png" alt="" width="24px" />
                <Typography
                  sx={{
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '500',
                    color: '#000',
                    marginTop: '8px',
                    marginBottom: '8px',
                  }}
                >
                  Career avenues {personality?.displayPersonalityType} will excel:
                </Typography>
                {personality?.careerAvenues?.map((career: string, index: number) => (
                  <Typography
                    key={index}
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: '400',
                      color: '#000000',
                      marginBottom: '4px',
                    }}
                  >
                    {career}
                  </Typography>
                ))}
              </Paper>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ marginBottom: '24px', textAlign: 'center', padding: '20px' }}>
          <Box
            sx={{
              backgroundColor: '#E6E4FE',
              padding: isMobile ? '16px' : '20px',
              textAlign: 'center',
              borderRadius: '10px',
              marginBottom: '20px',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: '600',
                fontSize: isMobile ? '18px' : '22px',
                color: '#1F2235',
                textAlign: 'center',
                marginBottom: '10px',
              }}
            >
              Well Balanced Personality
            </Typography>
            <img src="/images/connector-image.png" alt="No Personality" width={isMobile ? "150px" : "200px"} />
          </Box>
          <Typography variant="body2" sx={{ color: '#999' }}>
            Personality assessment data is not available for this candidate.
          </Typography>
        </Box>
      )}

      {/* Skills and Traits Section */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #E0E0E0',
          marginBottom: '24px',
          overflow: 'hidden',
        }}
      >
        {/* Table Header */}
        <Box
          sx={{
            backgroundColor: '#374151',
            padding: isMobile ? '12px 16px' : '16px 20px',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  color: 'white',
                  fontWeight: '600',
                  fontSize: isMobile ? '14px' : '16px',
                }}
              >
                Skills and Traits Factors
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography
                sx={{
                  color: 'white',
                  fontWeight: '600',
                  fontSize: isMobile ? '12px' : '14px',
                  textAlign: 'center',
                }}
              >
                Scores
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography
                sx={{
                  color: 'white',
                  fontWeight: '600',
                  fontSize: isMobile ? '12px' : '14px',
                  textAlign: 'center',
                }}
              >
                Grade
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Skills and Traits Rows */}
        {skillsTraits.length > 0 ? skillsTraits.map((skill, index) => (
          <Box key={index}>
            <Box sx={{ padding: isMobile ? '12px 16px' : '16px 20px' }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: isMobile ? '12px' : '14px',
                      marginBottom: '6px',
                    }}
                  >
                    {skill.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: isMobile ? '10px' : '12px',
                      color: '#6B7280',
                      lineHeight: 1.4,
                    }}
                  >
                    {skill.description}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography
                    sx={{
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: isMobile ? '14px' : '16px',
                      textAlign: 'center',
                      marginTop: isMobile ? '6px' : '0',
                    }}
                  >
                    {skill.score}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: isMobile ? '6px' : '0' }}>
                    <Box
                      sx={{
                        backgroundColor: getGradeColor(skill.grade),
                        padding: '4px 8px',
                        borderRadius: '4px',
                        color: 'white',
                        display: 'inline-block',
                        fontWeight: '600',
                        fontSize: isMobile ? '10px' : '12px',
                      }}
                    >
                      {skill.grade}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            {index < skillsTraits.length - 1 && (
              <Divider sx={{ borderColor: '#E0E0E0' }} />
            )}
          </Box>
        )) : (
          <Box sx={{ padding: '20px', textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: '#666', marginBottom: '8px' }}>
              No Skills and Traits Data Available
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Skills and traits assessment data is not available for this candidate.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Verification Scores Section */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #E0E0E0',
          padding: isMobile ? '20px' : '24px',
          marginBottom: '24px',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: '600',
            color: '#374151',
            fontSize: isMobile ? '16px' : '18px',
            marginBottom: '12px',
          }}
        >
          Verification Scores
        </Typography>

        <Box
          sx={{
            backgroundColor: '#F8F9FA',
            borderRadius: '8px',
            padding: '16px',
            border: '1px solid #E0E0E0',
            marginBottom: '16px',
          }}
        >
          {verificationScores.map((item, index) => {
            // Check if this item matches the actual score band
            const isActive = scoreBands.length > 0 && 
              item.label?.toLowerCase() === scoreBands[0]?.scoringBand?.toLowerCase();
            
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: index < verificationScores.length - 1 ? '1px solid #E0E0E0' : 'none',
                }}
              >
                <Typography
                  sx={{
                    fontSize: isMobile ? '11px' : '12px',
                    color: '#6B7280',
                  }}
                >
                  {item.label}
                </Typography>
                <Box
                  sx={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: isActive ? '#4CAF50' : '#9E9E9E',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  {isActive ? '✓' : '✗'}
                </Box>
              </Box>
            );
          })}
        </Box>

        {scoreBands.length > 0 && scoreBands[0]?.descriptors && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                width: '20px',
                height: '20px',
                backgroundColor: '#FF9800',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: '2px',
              }}
            >
              <Typography sx={{ color: 'white', fontSize: '12px' }}>💡</Typography>
            </Box>
            <Typography
              sx={{
                fontSize: isMobile ? '10px' : '12px',
                color: '#6B7280',
                lineHeight: 1.4,
              }}
            >
              {scoreBands[0].descriptors}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Footer Spacing */}
      <Box sx={{ height: '40px' }} />
    </Box>
    );
  } catch (error) {
    console.error('ReportContent rendering error:', error);
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Error loading report content
        </Typography>
        <Button onClick={onClose} variant="contained" sx={{ marginTop: '16px' }}>
          Close
        </Button>
      </Box>
    );
  }
};

// Main ReportView Component
const ReportView: React.FC<ReportViewProps> = ({ 
  open, 
  onClose, 
  candidateName = "Janki Sharma",
  applicantId,
  reportData,
  personalityData,
  topicScoreData,
  scoreBandData
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');

  console.log('ReportView render:', { open, isMobile, candidateName, reportData, applicantId });
  console.log('ReportView - open prop value:', open, 'type:', typeof open);

  // Handle undefined onClose
  const handleClose = onClose || (() => console.log('No close handler provided'));

  // Always render Dialog component - let MUI handle show/hide with open prop
  // This ensures proper portal rendering and state management

  // Mobile: Full-screen Dialog
  if (isMobile) {
    console.log('ReportView: rendering mobile Dialog', { open, isMobile, candidateName });
    console.log('Dialog will render with open=', open);
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        disableEscapeKeyDown={false}
        hideBackdrop={false}
        PaperProps={{
          sx: {
            margin: 0,
            maxHeight: '100vh',
            height: '100vh',
            borderRadius: 0,
            position: 'relative',
            zIndex: 1300,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1299,
          },
        }}
        sx={{
          zIndex: 1300,
          '& .MuiDialog-container': {
            alignItems: 'stretch',
          },
        }}
      >
        <ReportContent 
          candidateName={candidateName} 
          isMobile={isMobile} 
          onClose={handleClose} 
          applicantId={applicantId}
          reportData={reportData}
          personalityData={personalityData}
          topicScoreData={topicScoreData}
          scoreBandData={scoreBandData}
        />
      </Dialog>
    );
  }

  // Desktop: Right-side Drawer
  console.log('ReportView: rendering desktop Drawer');
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: '50vw',
          maxWidth: '50vw',
          minWidth: '50vw',
        },
      }}
    >
      <ReportContent 
        candidateName={candidateName} 
        isMobile={isMobile} 
        onClose={handleClose}
        applicantId={applicantId}
        reportData={reportData}
        personalityData={personalityData}
        topicScoreData={topicScoreData}
        scoreBandData={scoreBandData}
      />
    </Drawer>
  );
};

export default ReportView;
